import React, { useState, useEffect } from 'react';
import { signIn, getCurrentUser, signOut as amplifySignOut, confirmSignIn, resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Hub } from 'aws-amplify/utils';
import efsLogo from './assets/efs.png';

const LoginScreen = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetStep, setResetStep] = useState('request'); // 'request' or 'confirm'
  const [resetUsername, setResetUsername] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    // Validate inputs
    const newErrors = {};
    if (!formData.username.trim()) {
      newErrors.username = 'Username is required';
    }
    if (!formData.password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      const result = await signIn({
        username: formData.username,
        password: formData.password,
      });
      
      // Handle NEW_PASSWORD_REQUIRED challenge
      if (result.isSignedIn === false && result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        await confirmSignIn({
          challengeResponse: formData.password
        });
      }
      
    } catch (error) {
      console.error('Error signing in:', error);
      
      let errorMessage = 'Invalid username or password.';
      
      if (error.code === 'UserNotConfirmedException') {
        errorMessage = 'Account not confirmed.';
      } else if (error.code === 'PasswordResetRequiredException') {
        errorMessage = 'Password reset required.';
      } else if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found.';
      } else if (error.code === 'NotAuthorizedException') {
        errorMessage = 'Invalid username or password.';
      }
      
      setErrors({ general: errorMessage });
    }

    setIsLoading(false);
  };

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    if (!resetUsername.trim()) {
      setErrors({ resetUsername: 'Username is required' });
      setIsLoading(false);
      return;
    }

    try {
      await resetPassword({ username: resetUsername });
      setSuccessMessage('Password reset code sent to your email.');
      setResetStep('confirm');
    } catch (error) {
      console.error('Error requesting password reset:', error);
      let errorMessage = 'Failed to send reset code.';
      
      if (error.code === 'UserNotFoundException') {
        errorMessage = 'User not found.';
      } else if (error.code === 'LimitExceededException') {
        errorMessage = 'Too many attempts. Please try again later.';
      }
      
      setErrors({ general: errorMessage });
    }

    setIsLoading(false);
  };

  const handleForgotPasswordConfirm = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    const newErrors = {};
    if (!resetCode.trim()) {
      newErrors.resetCode = 'Reset code is required';
    }
    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsLoading(false);
      return;
    }

    try {
      await confirmResetPassword({
        username: resetUsername,
        confirmationCode: resetCode,
        newPassword: newPassword
      });
      setSuccessMessage('Password reset successful! You can now sign in.');
      setTimeout(() => {
        setShowForgotPassword(false);
        setResetStep('request');
        setResetUsername('');
        setResetCode('');
        setNewPassword('');
        setSuccessMessage('');
      }, 2000);
    } catch (error) {
      console.error('Error confirming password reset:', error);
      let errorMessage = 'Failed to reset password.';
      
      if (error.code === 'CodeMismatchException') {
        errorMessage = 'Invalid reset code.';
      } else if (error.code === 'ExpiredCodeException') {
        errorMessage = 'Reset code has expired.';
      } else if (error.code === 'InvalidPasswordException') {
        errorMessage = 'Password does not meet requirements.';
      }
      
      setErrors({ general: errorMessage });
    }

    setIsLoading(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    backgroundColor: 'white',
    border: '1px solid #d1d5db',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    outline: 'none',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box'
  };

  const getInputStyle = (fieldName) => ({
    ...inputStyle,
    border: errors[fieldName] ? '1px solid #ef4444' : '1px solid #d1d5db'
  });

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'linear-gradient(180deg, #1B3B6F 0%, #2A4A7F 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: '1rem'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '0.75rem',
        padding: '0',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: '#f8fafc',
          padding: '2rem 2rem 1.5rem 2rem',
          textAlign: 'center',
          borderBottom: '1px solid #e2e8f0'
        }}>
          <img
            src={efsLogo}
            alt="EFS Logo"
            style={{
              width: '280px',
              height: 'auto',
              marginBottom: '0.5rem',
              display: 'block',
              margin: '0 auto 0.5rem auto'
            }}
          />
          <p style={{
            fontSize: '1.125rem',
            color: '#64748b',
            margin: '0',
            fontWeight: '500'
          }}>
            Live Event Reporting
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '2rem' }}>
          {/* General Error */}
          {errors.general && (
            <div style={{
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#b91c1c',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              marginBottom: '1.5rem'
            }}>
              {errors.general}
            </div>
          )}

          {/* Username Field */}
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Username
            </label>
            <input
              type="text"
              value={formData.username}
              onChange={(e) => handleInputChange('username', e.target.value)}
              style={getInputStyle('username')}
              placeholder="Enter your username"
              onFocus={(e) => {
                e.target.style.borderColor = '#3b82f6';
                e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = errors.username ? '#ef4444' : '#d1d5db';
                e.target.style.boxShadow = 'none';
              }}
              disabled={isLoading}
            />
            {errors.username && (
              <p style={{
                color: '#ef4444',
                fontSize: '0.75rem',
                marginTop: '0.25rem',
                margin: '0.25rem 0 0 0'
              }}>
                {errors.username}
              </p>
            )}
          </div>

          {/* Password Field */}
          <div style={{ marginBottom: '2rem' }}>
            <label style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '500',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Password
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                style={getInputStyle('password')}
                placeholder="Enter your password"
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 2px rgba(59, 130, 246, 0.2)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = errors.password ? '#ef4444' : '#d1d5db';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '0.75rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'transparent',
                  border: 'none',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  fontSize: '0.875rem'
                }}
                disabled={isLoading}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            {errors.password && (
              <p style={{
                color: '#ef4444',
                fontSize: '0.75rem',
                marginTop: '0.25rem',
                margin: '0.25rem 0 0 0'
              }}>
                {errors.password}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            style={{
              width: '100%',
              background: isLoading ? '#9ca3af' : '#3b82f6',
              color: 'white',
              padding: '0.875rem 1rem',
              border: 'none',
              borderRadius: '0.375rem',
              fontSize: '0.875rem',
              fontWeight: '500',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.target.style.background = '#2563eb';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.target.style.background = '#3b82f6';
              }
            }}
          >
            {isLoading && (
              <div style={{
                width: '1rem',
                height: '1rem',
                border: '2px solid transparent',
                borderTop: '2px solid white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            )}
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>

          {/* Forgot Password Link */}
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#3b82f6',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                textDecoration: 'underline',
                padding: '0.5rem'
              }}
              onMouseEnter={(e) => {
                e.target.style.color = '#2563eb';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = '#3b82f6';
              }}
            >
              Forgot Password?
            </button>
          </div>
        </form>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000000,
          padding: '1rem'
        }}
        onClick={() => {
          setShowForgotPassword(false);
          setResetStep('request');
          setResetUsername('');
          setResetCode('');
          setNewPassword('');
          setErrors({});
          setSuccessMessage('');
        }}
        >
          <div style={{
            background: 'white',
            borderRadius: '0.75rem',
            padding: '0',
            width: '100%',
            maxWidth: '400px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            border: '1px solid #e2e8f0',
            overflow: 'hidden'
          }}
          onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              background: '#f8fafc',
              padding: '1.5rem 2rem',
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '1.25rem',
                fontWeight: '600',
                color: '#1a202c',
                margin: 0
              }}>
                Reset Password
              </h2>
              <button
                onClick={() => {
                  setShowForgotPassword(false);
                  setResetStep('request');
                  setResetUsername('');
                  setResetCode('');
                  setNewPassword('');
                  setErrors({});
                  setSuccessMessage('');
                }}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  color: '#64748b',
                  cursor: 'pointer',
                  padding: 0,
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div style={{ padding: '2rem' }}>
              {/* Success Message */}
              {successMessage && (
                <div style={{
                  background: '#f0fdf4',
                  border: '1px solid #86efac',
                  color: '#166534',
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem'
                }}>
                  {successMessage}
                </div>
              )}

              {/* General Error */}
              {errors.general && (
                <div style={{
                  background: '#fef2f2',
                  border: '1px solid #fecaca',
                  color: '#b91c1c',
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  marginBottom: '1.5rem'
                }}>
                  {errors.general}
                </div>
              )}

              {resetStep === 'request' ? (
                <form onSubmit={handleForgotPasswordRequest}>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    marginBottom: '1.5rem',
                    margin: '0 0 1.5rem 0'
                  }}>
                    Enter your username and we'll send you a password reset code via email.
                  </p>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Username
                    </label>
                    <input
                      type="text"
                      value={resetUsername}
                      onChange={(e) => {
                        setResetUsername(e.target.value);
                        if (errors.resetUsername) {
                          setErrors(prev => ({ ...prev, resetUsername: null }));
                        }
                      }}
                      style={{
                        ...inputStyle,
                        border: errors.resetUsername ? '1px solid #ef4444' : '1px solid #d1d5db'
                      }}
                      placeholder="Enter your username"
                      disabled={isLoading}
                    />
                    {errors.resetUsername && (
                      <p style={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {errors.resetUsername}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      background: isLoading ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1rem',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {isLoading ? 'Sending...' : 'Send Reset Code'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleForgotPasswordConfirm}>
                  <p style={{
                    fontSize: '0.875rem',
                    color: '#64748b',
                    marginBottom: '1.5rem',
                    margin: '0 0 1.5rem 0'
                  }}>
                    Enter the reset code sent to your email and your new password.
                  </p>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      Reset Code
                    </label>
                    <input
                      type="text"
                      value={resetCode}
                      onChange={(e) => {
                        setResetCode(e.target.value);
                        if (errors.resetCode) {
                          setErrors(prev => ({ ...prev, resetCode: null }));
                        }
                      }}
                      style={{
                        ...inputStyle,
                        border: errors.resetCode ? '1px solid #ef4444' : '1px solid #d1d5db'
                      }}
                      placeholder="Enter reset code"
                      disabled={isLoading}
                    />
                    {errors.resetCode && (
                      <p style={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {errors.resetCode}
                      </p>
                    )}
                  </div>

                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{
                      display: 'block',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      color: '#374151',
                      marginBottom: '0.5rem'
                    }}>
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => {
                        setNewPassword(e.target.value);
                        if (errors.newPassword) {
                          setErrors(prev => ({ ...prev, newPassword: null }));
                        }
                      }}
                      style={{
                        ...inputStyle,
                        border: errors.newPassword ? '1px solid #ef4444' : '1px solid #d1d5db'
                      }}
                      placeholder="Enter new password (min 8 characters)"
                      disabled={isLoading}
                    />
                    {errors.newPassword && (
                      <p style={{
                        color: '#ef4444',
                        fontSize: '0.75rem',
                        marginTop: '0.25rem',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {errors.newPassword}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    style={{
                      width: '100%',
                      background: isLoading ? '#9ca3af' : '#3b82f6',
                      color: 'white',
                      padding: '0.875rem 1rem',
                      border: 'none',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      fontWeight: '500',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      marginBottom: '0.75rem'
                    }}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setResetStep('request');
                      setResetCode('');
                      setNewPassword('');
                      setErrors({});
                    }}
                    style={{
                      width: '100%',
                      background: 'transparent',
                      color: '#64748b',
                      padding: '0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '0.375rem',
                      fontSize: '0.875rem',
                      cursor: 'pointer'
                    }}
                  >
                    Back to Username
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

// useAuth hook for AWS only
export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const AUTH_LOAD_MAX_MS = 6000; // fallback timeout to avoid indefinite grey screen
  const [loadStart] = useState(Date.now());

  const checkAuthState = async () => {
    try {
      const currentUser = await getCurrentUser();
      setUser(currentUser);
      setIsAuthenticated(true);
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log('[Auth] Initializing auth state check');
    checkAuthState();

    // Fallback timeout: if still loading after AUTH_LOAD_MAX_MS, force stop loading
    const timeoutId = setTimeout(() => {
      if (isLoading) {
        console.warn('[Auth] Loading exceeded timeout, forcing fallback to login screen');
        setIsLoading(false);
      }
    }, AUTH_LOAD_MAX_MS);

    const hubListener = (data) => {
      const { event } = data.payload;
      
      switch (event) {
        case 'signedIn':
          checkAuthState();
          break;
        case 'signedOut':
          setUser(null);
          setIsAuthenticated(false);
          break;
        case 'tokenRefresh_failure':
          setUser(null);
          setIsAuthenticated(false);
          break;
        default:
          break;
      }
    };

    const unsubscribe = Hub.listen('auth', hubListener);
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const signOut = async () => {
    try {
      await amplifySignOut();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return {
    isAuthenticated,
    isLoading,
    user,
    signOut
  };
};

export default LoginScreen;